import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Plus, Trash2, X } from "lucide-react";
import axios from "axios";
import { useAuth, useMessage } from "../../../../global-components/src";
import { PACOCA_API_URL } from "../../../../global-components/src/utils/vars";
import BottomSheet from "../../../bottom-sheet/bottom-sheet";
import { MessageSquareReply } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./style.css";

interface AutoReplyRule {
    id: string;
    keywords: string[];
    message: string;
}

interface CommentAutoReplyManagerProps {
    postId: number;
}

// ── The actual content (shared between PC modal and mobile bottom sheet) ─────
interface ContentProps {
    postId: number;
    open: boolean;
}

const CommentAutoReplyContent: React.FC<ContentProps> = ({ postId, open }) => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const { t } = useTranslation();
    const [rules, setRules] = useState<AutoReplyRule[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftKeyword, setDraftKeyword] = useState("");
    const [draftKeywords, setDraftKeywords] = useState<string[]>([]);
    const [draftMessage, setDraftMessage] = useState("");

    const fetchRules = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await axios.get(`${PACOCA_API_URL}/posts/${postId}/auto-reply-rules`, {
                headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
            });
            setRules(res.data.rules ?? []);
        } catch {
            // silently ignore
        } finally {
            setLoading(false);
        }
    }, [postId, token]);

    // Reset draft and reload rules every time the panel opens
    useEffect(() => {
        if (open) {
            setDraftKeyword("");
            setDraftKeywords([]);
            setDraftMessage("");
            fetchRules();
        }
    }, [open, fetchRules]);

    const addKeyword = () => {
        const kw = draftKeyword.trim().toLowerCase();
        if (!kw || draftKeywords.includes(kw) || draftKeywords?.length >= 10) return;
        setDraftKeywords(prev => [...prev, kw]);
        setDraftKeyword("");
    };

    const removeKeyword = (kw: string) => setDraftKeywords(prev => prev.filter(k => k !== kw));

    const saveRules = async (updated: AutoReplyRule[]) => {
        if (!token) return;
        setSaving(true);
        try {
            const res = await axios.put(
                `${PACOCA_API_URL}/posts/${postId}/auto-reply-rules`,
                { rules: updated },
                { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } }
            );
            setRules(res.data.rules ?? updated);
            setMessage(t("post.autoReply.saved"), "success");
        } catch (err: any) {
            const msg = err?.response?.data?.message || t("post.autoReply.errorSave");
            setMessage(msg, "error");
        } finally {
            setSaving(false);
        }
    };

    const addRule = async () => {
        if (draftKeywords?.length === 0 || !draftMessage.trim()) {
            setMessage(t("post.autoReply.errorKeywordMessage"), "error");
            return;
        }
        if (rules?.length >= 20) {
            setMessage(t("post.autoReply.errorLimit"), "error");
            return;
        }
        const newRule: AutoReplyRule = {
            id: crypto.randomUUID(),
            keywords: draftKeywords,
            message: draftMessage.trim(),
        };
        await saveRules([...rules, newRule]);
        setDraftKeywords([]);
        setDraftMessage("");
    };

    const deleteRule = (id: string) => saveRules(rules.filter(r => r.id !== id));

    return (
        <div className="arc-body">
            {/* Info banner */}
            <div className="arc-info-banner">
                {t("post.autoReply.infoBanner")}
            </div>

            {/* Rules list */}
            {loading ? (
                <div className="arc-empty">{t("post.autoReply.loading")}</div>
            ) : rules?.length === 0 ? (
                <div className="arc-empty">{t("post.autoReply.empty")}</div>
            ) : (
                <div className="arc-rules-list">
                    {rules.map(rule => (
                        <div key={rule.id} className="arc-rule-card">
                            <div className="arc-rule-top">
                                <div className="arc-rule-keywords">
                                    {rule.keywords.map(kw => (
                                        <span key={kw} className="arc-badge">{kw}</span>
                                    ))}
                                </div>
                                <button
                                    className="arc-rule-delete"
                                    onClick={() => deleteRule(rule.id)}
                                    disabled={saving}
                                    aria-label={t("post.autoReply.removeRule")}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="arc-rule-message">"{rule.message}"</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add new rule */}
            <div className="arc-divider" />
            <div className="arc-section-label">{t("post.autoReply.newRule")}</div>

            <div className="arc-field-group">
                <div className="arc-field-label">{t("post.autoReply.keywords")}</div>
                <div className="arc-kw-row">
                    <input
                        className="arc-input"
                        placeholder={t("post.autoReply.keywordPlaceholder")}
                        value={draftKeyword}
                        maxLength={50}
                        onChange={e => setDraftKeyword(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                    />
                    <button className="arc-kw-add-btn" onClick={addKeyword} type="button" aria-label={t("post.autoReply.keywords")}>
                        <Plus size={16} />
                    </button>
                </div>
                {draftKeywords?.length > 0 && (
                    <div className="arc-draft-keywords">
                        {draftKeywords.map(kw => (
                            <span key={kw} className="arc-badge arc-badge-draft">
                                {kw}
                                <button
                                    className="arc-badge-remove"
                                    onClick={() => removeKeyword(kw)}
                                    type="button"
                                    aria-label={t("post.autoReply.removeKeyword")}
                                >
                                    <X size={9} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="arc-field-group">
                <div className="arc-field-label">{t("post.autoReply.message")}</div>
                <textarea
                    className="arc-textarea"
                    placeholder={t("post.autoReply.messagePlaceholder")}
                    value={draftMessage}
                    maxLength={2000}
                    rows={3}
                    onChange={e => setDraftMessage(e.target.value)}
                />
            </div>

            <div className="foc-actions">
                <button
                    className="foc-btn foc-btn-primary"
                    onClick={addRule}
                    disabled={saving || draftKeywords?.length === 0 || !draftMessage.trim()}
                    type="button"
                >
                    {saving ? t("post.autoReply.saving") : t("post.autoReply.addRule")}
                </button>
            </div>
        </div>
    );
};

// ── Wrapper: PC modal (Bootstrap) + Mobile bottom sheet ───────────────────────
const CommentAutoReplyManager: React.FC<CommentAutoReplyManagerProps> = ({ postId }) => {
    const [open, setOpen] = useState(false);
    const closeModalRef = useRef<HTMLButtonElement | null>(null);
    const [pcModalOpen, setPcModalOpen] = useState(false);
    const location = useLocation();
    const { t } = useTranslation();
    const modalId = `modal-auto-reply-${postId}`;

    // Close bottom sheet on navigation
    useEffect(() => {
        if (open) setOpen(false);
    }, [location.pathname]);

    // Listen for Bootstrap modal open/close events (PC)
    useEffect(() => {
        const el = document.getElementById(modalId);
        if (!el) return;
        const onShow  = () => setPcModalOpen(true);
        const onHide  = () => setPcModalOpen(false);
        el.addEventListener("show.bs.modal", onShow);
        el.addEventListener("hidden.bs.modal", onHide);
        return () => {
            el.removeEventListener("show.bs.modal", onShow);
            el.removeEventListener("hidden.bs.modal", onHide);
        };
    }, [modalId]);

    return (
        <>
            {/* ── Menu item ─────────────────────────────────────────────── */}
            <li>
                {/* Desktop: opens Bootstrap modal, stop bubble so parent sheet stays open */}
                <span
                    className="dropdown-item cursor-pointer user-select-none none-cel"
                    data-bs-toggle="modal"
                    data-bs-target={`#${modalId}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="div-icon-fix">
                        <div className="div-icon-block">
                            <MessageSquareReply className="icon-config-post" size={16} />
                            {t("post.actions.autoReply")}
                        </div>
                    </div>
                </span>

                {/* Mobile: opens bottom sheet, stop bubble so parent sheet stays open */}
                <span
                    className="dropdown-item cursor-pointer user-select-none none-pc"
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                >
                    <div className="div-icon-fix">
                        <div className="div-icon-block">
                            <MessageSquareReply className="icon-config-post" size={16} />
                            {t("post.actions.autoReply")}
                        </div>
                    </div>
                </span>
            </li>

            {/* ── PC Bootstrap modal — portalled to <body> so it escapes the dropdown ── */}
            {ReactDOM.createPortal(
                <div
                    className="modal fade"
                    id={modalId}
                    aria-hidden="true"
                >
                    <div className="modal-dialog modal-dialog-centered foc-pc-dialog" role="document">
                        <div className="modal-content foc-pc-modal-content">
                            <div className="foc-pc-header">
                                <h4 className="foc-pc-title">{t("post.autoReply.title")}</h4>
                                <button
                                    ref={closeModalRef}
                                    type="button"
                                    className="foc-pc-close"
                                    data-bs-dismiss="modal"
                                    aria-label={t("post.autoReply.close")}
                                >
                                    ✕
                                </button>
                            </div>
                            <CommentAutoReplyContent postId={postId} open={pcModalOpen} />
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Mobile bottom sheet ───────────────────────────────────── */}
            <BottomSheet disableDrag={false} snapPoints={[0, 1]} initialSnap={1} setOpen={setOpen} open={open}>
                <div className="arc-sheet-content">
                    <div className="foc-sheet-header">
                        <h4 className="foc-sheet-title">{t("post.autoReply.title")}</h4>
                        <button
                            type="button"
                            className="foc-sheet-close"
                            onClick={() => setOpen(false)}
                            aria-label={t("post.autoReply.close")}
                        >
                            <X size={16} />
                        </button>
                    </div>
                    <div className="foc-sheet-body">
                        <CommentAutoReplyContent postId={postId} open={open} />
                    </div>
                </div>
            </BottomSheet>
        </>
    );
};

export default CommentAutoReplyManager;
